import { where } from "firebase/firestore";
import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { useMicrospecDynamicUIContext } from "../../MicrospecDynamicUI/GlobalProvider";
import { getDataFromCollectionWithQuery } from "../../MicrospecDynamicUI/Helpers/dataUtils";
import { useMicrospecThemeLanguageContext } from "../../MicrospecDynamicUI/Themes";

export interface MicrospecFlowModule {
    id: string;
    icon: string;
    name: string;
    description: string;
    link: string;
    autoDiscoveryEnabled: boolean;
}

interface MicrospecFlowModulesContextType {
    modules: MicrospecFlowModule[];
    modulesRetrieved: boolean;
}

const MicrospecFlowModulesContext = createContext<MicrospecFlowModulesContextType>({
    modules: [],
    modulesRetrieved: false,
});

interface MicrospecFlowModulesProviderProps {
    children: ReactNode;
    config: any;
}

const MicrospecFlowModulesProvider = ({ children, config }: MicrospecFlowModulesProviderProps) => {

    const { cloudProvider } = useMicrospecDynamicUIContext();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const { firestore } = cloudProvider;
    const { instance } = firestore;

    const [modules, setModules] = useState<MicrospecFlowModule[]>([]);
    const [modulesRetrieved, setModulesRetrieved] = useState(false);
    useMemo(async () => {
        const rawModules = await getDataFromCollectionWithQuery(null, null, instance, "modules", [
            where("autoDiscoveryEnabled", "==", true)
        ], true, 20);
        // order by order field if exists where 0 is highest priority
        rawModules.sort((a, b) => {
            const orderA = a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
            const orderB = b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
        });
        const processedModules = rawModules.map(module => ({
            id: module.id,
            icon: module.icon,
            name: localizationProvider.translateByMultiLangObject(module.name),
            description: localizationProvider.translateByMultiLangObject(module.description),
            link: module.link,
            autoDiscoveryEnabled: module.autoDiscoveryEnabled,
            canBeEnabled: module.canBeEnabled,
            order: module.order,
        }));
        setModules(processedModules);
        setModulesRetrieved(true);
    }, [localizationProvider, instance]);

    return (
        <MicrospecFlowModulesContext.Provider value={{ modules, modulesRetrieved }}>
            {children}
        </MicrospecFlowModulesContext.Provider>
    );
};

export const useMicrospecFlowModulesContext = () => {
    return useContext(MicrospecFlowModulesContext);
}

export default MicrospecFlowModulesProvider;